import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTheme } from '@material-ui/core/styles';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { fullMonth, fullDateISO } from 'Consts/DateFormats';
import { range } from 'Helpers/Utils';
import useRefDimensions from 'Hooks/useRefDimensions';
import useWindowSize from 'Hooks/useWindowSize';
import WeekdaysHeader from '../WeekdaysHeader';

const DAYS_OF_THE_WEEK = moment.weekdays();
const DAYS_IN_WEEK = DAYS_OF_THE_WEEK.length;

const styles = theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up(theme.dimensions.xl)]: {
      marginLeft: theme.spacing(18),
      marginRight: theme.spacing(18),
    },
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: theme.dimensions.xxl,
    borderLeft: '1px solid',
    borderLeftColor: theme.palette.grey[200],
    position: 'relative',
  },
});

const MonthlyCalendar = ({
  children,
  classes,
  items,
  date,
  onDayNumberClick,
  onDayClick,
  DayComponent,
  WeekdaysHeaderProps,
}) => {
  const theme = useTheme();
  const startOfMonth = moment.utc(date).startOf('month').startOf('week');
  const endOfMonth = moment.utc(date).endOf('month').endOf('week');

  const daysInMonth = endOfMonth.diff(startOfMonth, 'days');
  const firstDayOfTheMonth = moment(date).startOf('month').format('dddd');
  const startIndex = DAYS_OF_THE_WEEK.indexOf(firstDayOfTheMonth);

  const dateFullMonth = moment(date).format(fullMonth);
  const calendarRows = Math.ceil(daysInMonth / DAYS_IN_WEEK);
  const calendarGridSize = calendarRows * DAYS_IN_WEEK;

  const [calendarContainerRef, calendarContainerDimensions] = useRefDimensions(useWindowSize());

  const dayHeaderHeightTotal = calendarRows * theme.heights.monthlyCalendarDayHeaderHeight;
  const itemContainerHeight = ((calendarContainerDimensions.height - dayHeaderHeightTotal) / calendarRows);
  const itemSlots = Math.max(0, Math.floor(itemContainerHeight / theme.heights.monthlyCalendarDayItemHeight));

  const getDayItems = dateString => items.filter(item => item.date === dateString);

  // TODO: Move to cashflow
  const getBorderBottomType = dateString => items
    .filter(item => item.date === dateString && item.resource.attr('health'))
    .map(i => i.resource.attr('health'))
    .shift(); // [value] -> value

  return (
    <>
      <WeekdaysHeader {...WeekdaysHeaderProps} />
      <div className={classes.root} ref={calendarContainerRef}>
        <div className={classes.content}>
          {children}
          {range(calendarGridSize).map((i) => {
            const dayIndex = i - startIndex;
            const dayDate = moment(date).startOf('month').startOf('week').add(i, 'day');
            const dateString = dayDate.format(fullDateISO);
            const dayNumber = dayDate.date();
            return (
              <DayComponent
                key={`${dateFullMonth}-${dayNumber}-${dayIndex}`}
                day={dayNumber}
                date={moment(dayDate)}
                items={getDayItems(dateString)}
                borderBottomType={getBorderBottomType(dateString)}
                itemSlots={itemSlots}
                onDayNumberClick={onDayNumberClick}
                onDayClick={onDayClick}
                currentMonth={date}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};


MonthlyCalendar.defaultProps = {
  isLoading: null,
  onDayNumberClick: null,
  onDayClick: null,
  items: [],
};

MonthlyCalendar.propTypes = {
  isLoading: PropTypes.bool,
  date: PropTypes.instanceOf(moment).isRequired,
  items: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  DayComponent: PropTypes.object.isRequired,
  onDayNumberClick: PropTypes.func,
  onDayClick: PropTypes.func,
};

export default withStyles(styles)(MonthlyCalendar);
